import { useState, type FormEvent } from 'react';
import { RefreshCw, AlertTriangle, MapPin, Boxes, QrCode, ArrowRight, Lock, Unlock as UnlockIcon, Truck } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useParc, useInventaireParc, useMouvementsParc,
  useTransfererLotParc, useReserverLotParc,
  useLibererLotParc, useSortirLotParc,
} from '@/hooks/useBetonSprint2';
import { useCommandes } from '@/hooks/useBetonCommandes';
import { type LotProduction, TYPE_MVT_PARC_LABELS, type TypeMouvementParc } from '@/types';
import { formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

const fmtDT = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export const ParcPage = () => {
  const [zoneFilter, setZoneFilter] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [transferLot, setTransferLot] = useState<LotProduction | null>(null);
  const [reserveLot, setReserveLot]   = useState<LotProduction | null>(null);
  const [mvtsOpen, setMvtsOpen] = useState(false);

  const { data: inv,  isLoading: invLoading,  refetch: refetchInv  } = useInventaireParc();
  const { data: lots, isLoading: lotsLoading, refetch: refetchLots } = useParc({ zone: zoneFilter || undefined, limit: 200 });
  const liberer = useLibererLotParc();
  const sortir  = useSortirLotParc();
  const lotsData = lots?.data ?? [];

  const handle = async (fn: () => Promise<any>, msg?: string) => {
    if (msg && !(await demander.confirmer({ titre: msg, danger: /supprim|annul|rejet|refus|désactiv|écart/i.test(msg) }))) return;
    setActionError(null);
    try { await fn(); } catch (e) { setActionError(extractError(e).message); }
  };

  const refresh = () => { void refetchInv(); void refetchLots(); };

  return (
    <>
      <Topbar
        title="Parc préfabriqué"
        subtitle="Module 6 — Stockage zones + réservations commandes + traçabilité QR pièces"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={refresh}>
              <RefreshCw size={14} className={(invLoading || lotsLoading) ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-g" onClick={() => setMvtsOpen(true)}>
              <Boxes size={14} /> Historique mouvements
            </button>
          </>
        }
      />
      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        {/* Inventaire global */}
        {inv && (
          <div className="ds-kpi-strip">
            <div className="ds-ks ks-blue">
              <div className="ds-ks-label">Lots en parc</div>
              <div className="ds-ks-value">{inv.total.lots}</div>
              <div className="ds-ks-sub">Présents actuellement</div>
            </div>
            <div className="ds-ks ks-green">
              <div className="ds-ks-label">Quantité totale</div>
              <div className="ds-ks-value">{formatNumber(inv.total.quantite)}</div>
              <div className="ds-ks-sub">Pièces stockées</div>
            </div>
            <div className="ds-ks ks-orange">
              <div className="ds-ks-label">Réservés</div>
              <div className="ds-ks-value">{inv.total.reserves}</div>
              <div className="ds-ks-sub">Pour commandes</div>
            </div>
            <div className="ds-ks ks-purple">
              <div className="ds-ks-label">Zones actives</div>
              <div className="ds-ks-value">{inv.zones.length}</div>
              <div className="ds-ks-sub">Cliquer pour filtrer</div>
            </div>
          </div>
        )}

        {/* Grid zones */}
        {inv && inv.zones.length > 0 && (
          <div className="ds-table-panel" style={{ marginBottom: 16 }}>
            <div className="ds-table-header">
              <div>
                <div className="ds-table-title">Zones</div>
                <div className="ds-table-sub">Cliquer pour filtrer les lots</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, padding: 12 }}>
              <button onClick={() => setZoneFilter('')}
                style={{ padding: 12, borderRadius: 'var(--r-sm)',
                  border: `2px solid ${zoneFilter === '' ? 'var(--p)' : 'var(--bdr)'}`,
                  background: zoneFilter === '' ? 'rgba(0,127,71,0.06)' : 'transparent',
                  textAlign: 'left', cursor: 'pointer',
                }}>
                <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>Toutes zones</div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.total.lots} lots</div>
              </button>
              {inv.zones.map((z) => (
                <button key={z.zone} onClick={() => setZoneFilter(z.zone === '(sans zone)' ? '' : z.zone)}
                  style={{ padding: 12, borderRadius: 'var(--r-sm)',
                    border: `2px solid ${zoneFilter === z.zone ? 'var(--p)' : 'var(--bdr)'}`,
                    background: zoneFilter === z.zone ? 'rgba(0,127,71,0.06)' : 'transparent',
                    textAlign: 'left', cursor: 'pointer',
                  }}>
                  <div style={{ fontSize: 11, color: 'var(--tx-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <MapPin size={10} /> {z.zone}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{z.nbLots} lot{z.nbLots > 1 ? 's' : ''}</div>
                  <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{formatNumber(z.quantite)} pièces</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Lots en parc */}
        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Lots {zoneFilter ? `· Zone ${zoneFilter}` : ''}</div>
              <div className="ds-table-sub">{lotsData.length} lot{lotsData.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>N° Lot</th>
                <th>OF · Produit</th>
                <th className="ds-am" style={{ width: 90 }}>Quantité</th>
                <th style={{ width: 100 }}>Zone</th>
                <th style={{ width: 110 }}>Entrée parc</th>
                <th style={{ width: 140 }}>Réservé pour</th>
                <th style={{ width: 70 }}>QR</th>
                <th style={{ width: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lotsLoading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>
              )}
              {!lotsLoading && lotsData.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>
                    Aucun lot {zoneFilter ? `dans la zone ${zoneFilter}` : 'en parc'}
                  </td>
                </tr>
              )}
              {!lotsLoading && lotsData.map((lot: any) => (
                <tr key={lot.id}>
                  <td><span className="ds-ref-code">{lot.lotNumber}</span></td>
                  <td>
                    <div className="ds-entry-label">{lot.ordre?.produit?.designation ?? '—'}</div>
                    <div className="ds-entry-sub">{lot.ordre?.reference ?? '—'}</div>
                  </td>
                  <td className="ds-am">{formatNumber(parseFloat(lot.quantite))} {lot.ordre?.produit?.uniteVente ?? ''}</td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11 }}>
                      <MapPin size={10} /> {lot.zoneParc ?? '—'}
                    </span>
                    {lot.emplacementParc && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{lot.emplacementParc}</div>}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--tx-2)' }}>{lot.dateEntreeParc ? fmtDT(lot.dateEntreeParc) : '—'}</td>
                  <td>
                    {lot.commandeReservee ? (
                      <span style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>
                        <Lock size={10} style={{ display: 'inline', marginRight: 3 }} />
                        {lot.commandeReservee.reference}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--tx-3)' }}>—</span>
                    )}
                  </td>
                  <td>
                    <a href={lot.qrCode} target="_blank" rel="noopener noreferrer" className="ds-row-btn">
                      <QrCode size={13} />
                    </a>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setTransferLot(lot)} title="Transférer">
                        <ArrowRight size={11} />
                      </button>
                      {!lot.commandeReserveeId ? (
                        <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setReserveLot(lot)} title="Réserver" style={{ color: 'var(--info)' }}>
                          <Lock size={11} />
                        </button>
                      ) : (
                        <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => handle(() => liberer.mutateAsync(lot.id), `Libérer ${lot.lotNumber} ?`)} title="Libérer" style={{ color: 'var(--tx-3)' }}>
                          <UnlockIcon size={11} />
                        </button>
                      )}
                      <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => handle(async () => { const motif = await demander.texte({ titre: 'Motif sortie ?', confirmer: 'Sortir' }); return sortir.mutateAsync({ lotId: lot.id, payload: { motif: motif ?? undefined } }); }, `Sortir ${lot.lotNumber} ?`)} title="Sortir parc" style={{ color: 'var(--warning)' }}>
                        <Truck size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TransfertDialog lot={transferLot} onClose={() => setTransferLot(null)} />
      <ReserverDialog  lot={reserveLot}  onClose={() => setReserveLot(null)} />
      <MouvementsDialog open={mvtsOpen} onClose={() => setMvtsOpen(false)} />
    </>
  );
};

// ── Dialog transfert ────────────────────────────────────

const TransfertDialog = ({ lot, onClose }: { lot: LotProduction | null; onClose: () => void }) => {
  const open = lot !== null;
  const [zoneTo, setZoneTo] = useState('');
  const [empl, setEmpl]     = useState('');
  const [motif, setMotif]   = useState('');
  const [error, setError]   = useState<string | null>(null);
  const transferer = useTransfererLotParc();

  if (!lot) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await transferer.mutateAsync({ lotId: lot.id, payload: { zoneTo, emplacementParc: empl || undefined, motif: motif || undefined } });
      setZoneTo(''); setEmpl(''); setMotif('');
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Transférer ${lot.lotNumber}`} subtitle={`Actuellement zone ${lot.zoneParc ?? '?'}`} size="sm">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
        <Field label="Zone destination *">
          <input value={zoneTo} onChange={(e) => setZoneTo(e.target.value)} required placeholder="B2" className="ds-fi" style={{ width: '100%' }} />
        </Field>
        <Field label="Emplacement (optionnel)">
          <input value={empl} onChange={(e) => setEmpl(e.target.value)} placeholder="Rangée 3 niveau 2" className="ds-fi" style={{ width: '100%' }} />
        </Field>
        <Field label="Motif">
          <input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Préparation expédition" className="ds-fi" style={{ width: '100%' }} />
        </Field>
        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!zoneTo || transferer.isPending}>
            <ArrowRight size={14} /> Transférer
          </button>
        </div>
      </form>
    </Dialog>
  );
};

// ── Dialog réserver ─────────────────────────────────────

const ReserverDialog = ({ lot, onClose }: { lot: LotProduction | null; onClose: () => void }) => {
  const open = lot !== null;
  const [commandeId, setCmdId] = useState('');
  const [motif, setMotif]      = useState('');
  const [error, setError]      = useState<string | null>(null);
  const reserver = useReserverLotParc();
  const { data: cmds } = useCommandes({ statut: 'confirmee', limit: 100 });

  if (!lot) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await reserver.mutateAsync({ lotId: lot.id, payload: { commandeId, motif: motif || undefined } });
      setCmdId(''); setMotif('');
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Réserver ${lot.lotNumber}`} subtitle="Lie le lot à une commande client" size="sm">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
        <Field label="Commande confirmée *">
          <select value={commandeId} onChange={(e) => setCmdId(e.target.value)} required className="ds-fi" style={{ width: '100%' }}>
            <option value="">— Sélectionner —</option>
            {cmds?.data.map((c) => (
              <option key={c.id} value={c.id}>
                {c.reference} · {c.client?.companyName ?? c.client?.fullName ?? '—'}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Motif">
          <input value={motif} onChange={(e) => setMotif(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
        </Field>
        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!commandeId || reserver.isPending}>
            <Lock size={14} /> Réserver
          </button>
        </div>
      </form>
    </Dialog>
  );
};

// ── Dialog historique mouvements ────────────────────────

const MouvementsDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [typeFilter, setType] = useState<TypeMouvementParc | ''>('');
  const { data } = useMouvementsParc({ type: typeFilter || undefined, limit: 100 });

  return (
    <Dialog open={open} onClose={onClose} title="Historique mouvements parc" subtitle="Audit complet (entrées · transferts · réservations · sorties)" size="lg">
      <div className="space-y-3">
        <div>
          <select className="ds-fi" value={typeFilter} onChange={(e) => setType(e.target.value as TypeMouvementParc | '')} style={{ width: 'auto' }}>
            <option value="">Tous types</option>
            {(Object.keys(TYPE_MVT_PARC_LABELS) as TypeMouvementParc[]).map((t) => (
              <option key={t} value={t}>{TYPE_MVT_PARC_LABELS[t]}</option>
            ))}
          </select>
        </div>
        <table className="ds-tbl">
          <thead>
            <tr>
              <th style={{ width: 130 }}>Quand</th>
              <th style={{ width: 130 }}>Type</th>
              <th>Lot</th>
              <th>Zone</th>
              <th>Motif</th>
            </tr>
          </thead>
          <tbody>
            {(data?.data ?? []).map((m) => (
              <tr key={m.id}>
                <td style={{ fontSize: 11, color: 'var(--tx-2)' }}>{fmtDT(m.createdAt)}</td>
                <td><span className="ds-bd ds-bd-i">{TYPE_MVT_PARC_LABELS[m.type]}</span></td>
                <td><span className="ds-ref-code">{m.lot?.lotNumber ?? '—'}</span></td>
                <td style={{ fontSize: 11 }}>
                  {m.zoneFrom && <span>{m.zoneFrom}</span>}
                  {m.zoneFrom && m.zoneTo && <span style={{ color: 'var(--tx-3)' }}> → </span>}
                  {m.zoneTo}
                </td>
                <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{m.motif ?? '—'}</td>
              </tr>
            ))}
            {(data?.data ?? []).length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucun mouvement</td></tr>
            )}
          </tbody>
        </table>
        <div className="flex justify-end pt-2 border-t">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
